import Keycloak, { KeycloakTokenParsed } from 'keycloak-js';
import { getEnvironmentVariable, logInfo, logError } from '../utils';

/**
 * Service for handling Keycloak authentication
 */
export class KeycloakService {
  private keycloak: Keycloak | null = null;
  private initialized = false;
  private refreshInterval: number | null = null;

  /**
   * Initialize the Keycloak instance
   * @returns Promise resolving to true if initialization was successful
   */
  async init(): Promise<boolean> {
    if (this.initialized) {
      return true;
    }

    try {
      const authority = getEnvironmentVariable('AUTHORITY') as string;
      const realm = getEnvironmentVariable('REALM') as string;
      const clientId = getEnvironmentVariable('CLIENT_ID') as string;

      if (!authority || !realm || !clientId) {
        logError('Keycloak configuration is missing. Check environment variables.');
        return false;
      }

      this.keycloak = new Keycloak({
        url: authority,
        realm: realm,
        clientId: clientId,
      });

      const authenticated = await this.keycloak.init({
        onLoad: 'login-required',
      });

      this.initialized = true;

      if (authenticated) {
        this.setupTokenRefresh();
      }

      logInfo('Keycloak initialized successfully');
      return true;
    } catch (error) {
      logError('Failed to initialize Keycloak:', error);
      return false;
    }
  }

  /**
   * Get the Keycloak instance
   * @returns The Keycloak instance
   * @throws Error if Keycloak is not initialized
   */
  getKeycloak(): Keycloak {
    if (!this.keycloak) {
      throw new Error('Keycloak is not initialized');
    }
    return this.keycloak;
  }

  /**
   * Check if the user is authenticated
   * @returns True if the user is authenticated
   */
  isAuthenticated(): boolean {
    return !!this.keycloak?.authenticated;
  }

  /**
   * Get the authentication token
   * @returns The authentication token or null if not authenticated
   */
  getToken(): string | null {
    return this.keycloak?.token ?? null;
  }

  /**
   * Get the refresh token
   * @returns The refresh token or null if not authenticated
   */
  getRefreshToken(): string | null {
    return this.keycloak?.refreshToken ?? null;
  }

  /**
   * Get the token parsed as JWT
   * @returns The parsed token or null if not authenticated
   */
  getTokenParsed(): KeycloakTokenParsed | null {
    return this.keycloak?.tokenParsed ?? null;
  }

  /**
   * Login the user
   * @param options Optional login options
   * @returns Promise resolving when login is complete
   */
  login(options?: Keycloak.KeycloakLoginOptions): Promise<void> {
    if (!this.keycloak) {
      return Promise.reject(new Error('Keycloak is not initialized'));
    }
    return this.keycloak.login(options);
  }

  /**
   * Logout the user
   * @param options Optional logout options
   * @returns Promise resolving when logout is complete
   */
  logout(options?: Keycloak.KeycloakLogoutOptions): Promise<void> {
    if (!this.keycloak) {
      return Promise.reject(new Error('Keycloak is not initialized'));
    }

    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
      this.refreshInterval = null;
    }

    return this.keycloak.logout(options);
  }

  /**
   * Update the token
   * @param minValidity Minimum validity time in seconds
   * @returns Promise resolving to true if the token was updated
   */
  async updateToken(minValidity: number = 5): Promise<boolean> {
    if (!this.keycloak) {
      return Promise.reject(new Error('Keycloak is not initialized'));
    }

    try {
      return await this.keycloak.updateToken(minValidity);
    } catch (error) {
      logError('Failed to update token:', error);
      return false;
    }
  }

  /**
   * Setup automatic token refresh
   */
  private setupTokenRefresh(): void {
    if (!this.keycloak) {
      return;
    }

    // Clear any existing interval
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
    }

    // Setup token refresh
    this.refreshInterval = window.setInterval(async () => {
      try {
        const updated = await this.updateToken(70); // Refresh when token has less than 70 seconds left
        if (updated) {
          logInfo('Token refreshed successfully');
        }
      } catch (error) {
        logError('Failed to refresh token:', error);
        // If token refresh fails, redirect to login
        this.login().catch(reason => {
          logError('Error redirecting to login.', reason);
        });
      }
    }, 60000); // Check every minute
  }

  /**
   * Register a callback for token expiration
   * @param callback Function to call when token expires
   */
  onTokenExpired(callback: () => void): void {
    if (!this.keycloak) {
      return;
    }

    this.keycloak.onTokenExpired = callback;
  }

  /**
   * Register a callback for authentication success
   * @param callback Function to call when authentication succeeds
   */
  onAuthSuccess(callback: () => void): void {
    if (!this.keycloak) {
      return;
    }

    this.keycloak.onAuthSuccess = callback;
  }

  /**
   * Register a callback for authentication error
   * @param callback Function to call when authentication fails
   */
  onAuthError(callback: () => void): void {
    if (!this.keycloak) {
      return;
    }

    this.keycloak.onAuthError = callback;
  }

  /**
   * Register a callback for token expiration
   * @param callback Function to call when token expires
   */
  onAuthRefreshSuccess(callback: () => void): void {
    if (!this.keycloak) {
      return;
    }

    this.keycloak.onAuthRefreshSuccess = callback;
  }

  /**
   * Register a callback for token refresh error
   * @param callback Function to call when token refresh fails
   */
  onAuthRefreshError(callback: () => void): void {
    if (!this.keycloak) {
      return;
    }

    this.keycloak.onAuthRefreshError = callback;
  }

  /**
   * Register a callback for logout
   * @param callback Function to call when logout occurs
   */
  onAuthLogout(callback: () => void): void {
    if (!this.keycloak) {
      return;
    }

    this.keycloak.onAuthLogout = callback;
  }
}

// Create a singleton instance
const keycloakService = new KeycloakService();

export default keycloakService;
